import { useContext, useEffect, useRef, useState } from 'react'
import { Panel } from '@xyflow/react'
import { DndProvider } from 'react-dnd'
import { HTML5Backend } from 'react-dnd-html5-backend'
import { ThemeProvider } from 'styled-components'
import { Box, IconButton, Tooltip } from '@mui/material'
import MoreTimeIcon from '@mui/icons-material/MoreTime'
import SwapVertIcon from '@mui/icons-material/SwapVert'
import PlayArrowIcon from '@mui/icons-material/PlayArrow'
import StopIcon from '@mui/icons-material/Stop'
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome'
import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined'
import FileDownloadIcon from '@mui/icons-material/FileDownload'
import FileUploadIcon from '@mui/icons-material/FileUpload'
import Flow from '../../components/features/playbooks/ReactFlowChart'
import Sidebar, { SidebarMode } from '../../components/features/playbooks/Sidebar'
import PlaybookScheduler from '../../components/features/Modals/PlaybookScheduler'
import PlaybookRunModal from '../../components/features/Modals/PlaybookRunModal'
import { useToast } from '../../contexts/toast.context'
import { PlaybookContext } from '../../contexts/playbooks.context'
import { SchedulerContext } from '../../contexts/schedulers.context'
import { updatePlaybook, exportPlaybookYaml, importPlaybookYaml } from '../../services/playbook.service'
import { createScheduler } from '../../services/scheduler.service'
import { darkTheme, lightTheme } from '../../themes/theme'
import { DirectionTypes, IPlaybook } from '../../types/playbook.type'
import { ISchedulerOptions } from '../../types/scheduler.type'
import { AppConfigContext } from '../../contexts/app-config.context'

import 'reactflow/dist/style.css'
import styles from './styles.module.scss'
import Header from '../../components/features/playbooks/Header'

export default function PlaybookPage() {
  const [sidebarMode, setSidebarMode] = useState<SidebarMode | null>(null)
  const [isPlayed, setIsPlayed] = useState(false)
  const [isScheduling, setIsScheduling] = useState(false)
  const [isLayoutEditing, setIsLayoutEditing] = useState(false)

  const { getSchedulers, schedulers, setSchedulers } = useContext(SchedulerContext)

  const { playbook, setPlaybook, handleUpdate, setReactFlowNodes, savePlaybook, getPlaybook } =
    useContext(PlaybookContext)
  const { isLightMode } = useContext(AppConfigContext)

  const fileInputRef = useRef<HTMLInputElement>(null)
  const { showToast } = useToast()

  const theme = isLightMode ? lightTheme : darkTheme

  const togglePlay = () => {
    setIsPlayed(!isPlayed)
  }

  const handleAutoLayout = () => {
    setIsLayoutEditing(!isLayoutEditing)
  }

  const handleChangeDirection = async () => {
    if (playbook) {
      const newPlaybook = { ...playbook }

      if (playbook.direction === DirectionTypes.HORIZONTAL) {
        newPlaybook.direction = DirectionTypes.VERTICAL
      } else {
        newPlaybook.direction = DirectionTypes.HORIZONTAL
      }

      // reset playbook view for re-initializing node handles
      setPlaybook(undefined)
      setReactFlowNodes([])

      savePlaybook(newPlaybook)
    }
  }

  const onAdd = async (playbook: IPlaybook) => {
    if (playbook) {
      const data = await updatePlaybook(playbook, true)

      if (data.success && data.data) {
        setPlaybook(() => data.data)
        handleUpdate(data.data)
        showToast('success', data.message)
      }
    }
  }

  const handleSchedule = async (schedulerOptions: ISchedulerOptions) => {
    if (playbook) {
      const data = await createScheduler(schedulerOptions.frequency, playbook.id)

      if (data.data) {
        showToast('success', data.message)
        setSchedulers([...schedulers, data.data])
        setIsScheduling(false)
      }
    }
  }

  const handleImportYaml = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file) return

    const reader = new FileReader()
    reader.onload = async (e) => {
      const yaml = e.target?.result as string
      const resp = await importPlaybookYaml(yaml)
      if (resp.success) {
        showToast('success', resp.message || 'Playbook imported successfully')
        if (playbook) {
          getPlaybook(playbook.id.toString())
        }
      }
    }
    reader.readAsText(file)
    event.target.value = ''
  }

  useEffect(() => {
    if (playbook) {
      getSchedulers(playbook.id)
    }
  }, [playbook, playbook?.id])

  useEffect(() => {
    const elements = document.body.getElementsByClassName('leva-c-kWgxhW')
    const leva = elements[0] as HTMLElement

    if (leva) {
      if (isLayoutEditing) {
        leva.style.setProperty('display', 'block', 'important')
      } else {
        leva.style.setProperty('display', 'none', 'important')
      }
    }
  }, [isLayoutEditing])

  return (
    <DndProvider backend={HTML5Backend}>
      <Header playbook={playbook} sidebarMode={sidebarMode} onSidebarModeChange={setSidebarMode} />
      <Box className={styles.playbookPage}>
        <ThemeProvider theme={theme}>
          <Flow isLightMode={isLightMode} onAdd={onAdd} isLayoutEditing={isLayoutEditing}>
            <>
              <Panel
                position="top-left"
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  margin: '5px',
                }}
              >
                <Tooltip
                  title={playbook?.status === 'running' ? 'Stop playbook' : 'Run playbook'}
                  disableInteractive
                  placement="right"
                >
                  <IconButton onClick={togglePlay}>
                    {playbook?.status === 'running' ? <StopIcon id="stop" /> : <PlayArrowIcon id="play" />}
                  </IconButton>
                </Tooltip>

                <Tooltip title="Schedule playbook" disableInteractive placement="right">
                  <IconButton onClick={() => setIsScheduling(true)}>
                    <MoreTimeIcon />
                  </IconButton>
                </Tooltip>

                <Tooltip title="Switch direction of connections" disableInteractive placement="right">
                  <IconButton onClick={handleChangeDirection}>
                    <SwapVertIcon
                      className={
                        playbook?.direction === DirectionTypes.HORIZONTAL ? styles.horizontal : styles.vertical
                      }
                    />
                  </IconButton>
                </Tooltip>

                <Tooltip title="Automatic layout" disableInteractive placement="right">
                  <IconButton onClick={handleAutoLayout}>
                    {isLayoutEditing ? <AutoAwesomeIcon /> : <AutoAwesomeOutlinedIcon />}
                  </IconButton>
                </Tooltip>

                <Tooltip title="Upload YAML" disableInteractive placement="right">
                  <IconButton onClick={() => fileInputRef.current?.click()}>
                    <FileUploadIcon />
                  </IconButton>
                </Tooltip>
                <input
                  type="file"
                  ref={fileInputRef}
                  accept=".yaml,.yml"
                  style={{ display: 'none' }}
                  onChange={handleImportYaml}
                />

                <Tooltip title="Download YAML" disableInteractive placement="right">
                  <IconButton onClick={() => playbook && exportPlaybookYaml(playbook.id)}>
                    <FileDownloadIcon />
                  </IconButton>
                </Tooltip>
              </Panel>
            </>
          </Flow>
          <PlaybookRunModal playbook={playbook} isOpen={isPlayed} onClose={() => setIsPlayed(false)} />
          <PlaybookScheduler
            isOpen={isScheduling}
            playbookName={playbook?.name}
            isLightMode={isLightMode}
            onClose={() => setIsScheduling(false)}
            handleSchedule={handleSchedule}
          />
        </ThemeProvider>
        <Sidebar mode={sidebarMode} />
      </Box>
    </DndProvider>
  )
}
